import React from 'react';
import { SkeletonLoading } from '@saleshandy/design-system';
import {
  Like,
  Dislike,
  Clock,
  PhoneCross,
  RecordMail,
  HandStop,
} from '@saleshandy/icons';
import { CallOutcomeContentTabs, SystemCallOutcomeName } from '../../../enums';
import { OutcomeSentimentBGColor } from '../../../../../enums/custom-outcome';
import { CallOutcome } from '../../../types';
import hasPermission from '../../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../../shared/utils/access-control/enums/permissions';
import { PhoneStar } from '../../../../../../../shared/svg/phone-star';
import { PhoneSlash } from '../../../../../../../shared/svg/phone-slash';
import { GlobeCross } from '../../../../../../../shared/svg/globe-cross';

export const getCallOutcomeSystemIcon = (
  outcomeName: string,
  defaultProps: { height?: number; width?: number } = {},
) => {
  const iconSize = {
    height: defaultProps.height || 16,
    width: defaultProps.width || 16,
  };

  switch (outcomeName) {
    case SystemCallOutcomeName.Interested:
      return <Like {...iconSize} className="gray-txt-15" />;
    case SystemCallOutcomeName.CallbackLater:
      return <Clock {...iconSize} className="gray-txt-15" />;
    case SystemCallOutcomeName.NotInterested:
      return <Dislike {...iconSize} className="gray-txt-15" />;
    case SystemCallOutcomeName.Followup:
      return <Like {...iconSize} className="gray-txt-15" />;
    case SystemCallOutcomeName.WrongNumber:
      return <PhoneCross {...iconSize} className="gray-txt-15" />;
    case SystemCallOutcomeName.NoAnswer:
      return <PhoneCross {...iconSize} className="gray-txt-15" />;
    case SystemCallOutcomeName.LeftVoiceMail:
      return <RecordMail {...iconSize} className="gray-txt-15" />;
    case SystemCallOutcomeName.NotInService:
      return <GlobeCross {...iconSize} className="gray-txt-15" />;
    case SystemCallOutcomeName.GateKeeper:
      return <HandStop {...iconSize} className="gray-txt-15" />;
    case SystemCallOutcomeName.DoNotContact:
      return <PhoneSlash {...iconSize} className="gray-txt-15" />;
    default:
      return <PhoneStar {...iconSize} className="gray-txt-15" />;
  }
};

export const getCallOutcomeIcon = (
  name: string,
  isDefault: boolean,
  sentiment: string,
  defaultProps: {
    height?: number;
    width?: number;
    isColorsIcon?: boolean;
  } = {},
) => {
  if (!isDefault) {
    return (
      <PhoneStar
        height={defaultProps.height || 16}
        width={defaultProps.width || 16}
        className="gray-txt-15"
      />
    );
  }
  return getCallOutcomeSystemIcon(name, defaultProps);
};

export const getCallOutcomeColumns = () => [
  {
    dataField: 'processedName',
    text: 'Outcome Name',
    component: (cell) => {
      if (cell) {
        const { name, sentiment, isDefault } = cell;
        return (
          <div className="call-outcome-name-container">
            <div className="call-outcome-name-content">
              <div>
                {getCallOutcomeIcon(name, isDefault, sentiment, {
                  width: 16,
                  height: 16,
                })}
              </div>
              <div>{name}</div>
            </div>
          </div>
        );
      }
      return '-';
    },
    headerLoadingSkeleton: <SkeletonLoading width={54} height={14} />,
    cellLoadingSkeleton: <SkeletonLoading width={72} height={14} />,
  },
  {
    dataField: 'processedSentiment',
    text: 'Sentiment',
    component: (cell) => {
      if (cell) {
        const { sentiment, bgColor, textColor } = cell;
        return (
          <div
            className="call-outcome-sentiment-badge"
            style={{ background: bgColor, color: textColor }}
          >
            <div
              className="call-outcome-sentiment-dot"
              style={{ background: OutcomeSentimentBGColor[sentiment] }}
            />
            <div className="call-outcome-sentiment-text">{sentiment}</div>
          </div>
        );
      }
      return '-';
    },
    headerLoadingSkeleton: <SkeletonLoading width={34} height={14} />,
    cellLoadingSkeleton: <SkeletonLoading width={72} height={14} />,
  },
];

export const generateCallOutcomeActionsColumn = (tab: CallOutcomeContentTabs) =>
  tab === CallOutcomeContentTabs.CustomOutcomes &&
  (hasPermission(Permissions.CALL_OUTCOME_UPDATE) ||
    hasPermission(Permissions.CALL_OUTCOME_DELETE))
    ? [
        hasPermission(Permissions.CALL_OUTCOME_UPDATE) && {
          displayName: 'Edit',
          icon: 'pencil',
          key: 'edit',
          position: 'out',
        },
        hasPermission(Permissions.CALL_OUTCOME_DELETE) && {
          displayName: 'Delete',
          icon: 'trash',
          key: 'delete',
          position: 'out',
        },
      ].filter((obj) => obj)
    : null;

export const getCallOutcomeTableWrapper = (
  tab: CallOutcomeContentTabs,
  customCount: number,
  defaultCount: number,
) => {
  switch (tab) {
    case CallOutcomeContentTabs.CustomOutcomes:
      return customCount > 25 ? 'paginated-table' : '';
    case CallOutcomeContentTabs.SystemOutcomes:
      return defaultCount > 25 ? 'paginated-table' : '';
    default:
      return '';
  }
};

export const getDeleteCallOutcomeDropdownOptions = (
  allOutcomes: CallOutcome[],
  actionOutcomeId: number,
) => {
  const options = allOutcomes
    .filter((outcome) => outcome.id !== actionOutcomeId)
    .map((outcome) => ({
      key: outcome.id,
      name: outcome.name,
      sentiment: outcome.sentiment,
      bgColor: outcome.bgColor,
      textColor: outcome.textColor,
      isDefault: outcome.isDefault === 1,
    }));
  return options.sort((a, b) => a.key - b.key);
};
